/**
 * Inline SVG icons. Stroke-based, 24x24 viewBox, currentColor so they
 * inherit text colour. Keep these tiny and consistent — full icon
 * libraries are out of scope.
 */
import type { SVGProps } from 'react';

const baseProps: SVGProps<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

export function IconDashboard(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  );
}

export function IconBell(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

export function IconRefresh(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

export function IconClose(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function IconMenu(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

export function IconPipeline(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M3 4h18l-7 8v7l-4 2v-9z" />
    </svg>
  );
}

export function IconBusiness(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M3 21h18M6 21V10M12 21V4M18 21v-7" />
    </svg>
  );
}

export function IconArollSaas(props: SVGProps<SVGSVGElement>) {
  // The ▲ROLL mark as a stroke triangle — consistent with the sidebar's
  // monochrome outline grammar.
  return (
    <svg {...baseProps} {...props}>
      <path d="M12 5l8 14H4z" />
    </svg>
  );
}

export function IconClients(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

// Slate / clipboard glyph - signals the production-crew roster.
export function IconContractors(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 4v2h8V4" />
      <path d="M7 11h10" />
      <path d="M7 15h6" />
    </svg>
  );
}

// Briefcase glyph for the Jobs index — signals "every job in the
// system" without colliding with IconContractors (which is a slate /
// clipboard for the crew roster).
export function IconJobs(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M3 13h18" />
    </svg>
  );
}

/** Timelines: stacked Gantt bars with a delivery point. */
export function IconTimelines(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M3 6h9" />
      <path d="M7 12h9" />
      <path d="M5 18h6" />
      <path d="m17.5 15.5 2.5 2.5-2.5 2.5L15 18Z" />
    </svg>
  );
}

/** Tasks: a checklist — one ticked row, two waiting. */
export function IconTasks(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="m3 6.5 1.5 1.5L7 5.5" />
      <path d="M11 6.5h10" />
      <rect x="3" y="11" width="4" height="4" rx="1" />
      <path d="M11 13h10" />
      <rect x="3" y="17.5" width="4" height="4" rx="1" />
      <path d="M11 19.5h10" />
    </svg>
  );
}

export function IconInbox(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}

/** Launch: a paper plane, nose up-right. */
export function IconLaunch(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M21 3 3 10.5l7.5 3L13.5 21 21 3Z" />
      <path d="M10.5 13.5 21 3" />
    </svg>
  );
}

export function IconSettings(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

export function IconSignOut(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export function IconFolio(props: SVGProps<SVGSVGElement>) {
  // Image / portfolio: a frame with a mountain + sun inside reads as
  // "gallery of work" without overloading the existing icon set.
  return (
    <svg {...baseProps} {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="M3 17l4-4 4 4 4-4 6 6" />
    </svg>
  );
}

export function IconAccounting(props: SVGProps<SVGSVGElement>) {
  // Receipt with a small $ glyph — reads as quotes + invoices without
  // implying a calculator or chart context.
  return (
    <svg {...baseProps} {...props}>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 9h6" />
      <path d="M9 13h6" />
    </svg>
  );
}

export function IconSubscriptions(props: SVGProps<SVGSVGElement>) {
  // A recurring-loop arrow — reads as "recurring / renews" rather than a
  // one-off receipt (which is the Accounting icon above).
  return (
    <svg {...baseProps} {...props}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v4h-4" />
    </svg>
  );
}

export function IconDocs(props: SVGProps<SVGSVGElement>) {
  // A single page with a folded corner and two text lines: "a written
  // procedure", distinct from the receipt (Accounting) and the mail
  // envelope (Inbox / Enquiries).
  return (
    <svg {...baseProps} {...props}>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 12h6" />
      <path d="M9 16h6" />
    </svg>
  );
}

export function IconCoach(props: SVGProps<SVGSVGElement>) {
  // A whistle on a cord: the coach. Kept simple at 20px.
  return (
    <svg {...baseProps} {...props}>
      <circle cx="9" cy="14" r="5" />
      <path d="M13.5 11.5L20 8v4l-6 1.5" />
      <path d="M9 14v-1" />
      <path d="M4 5c3 0 5 1.5 5 4" />
    </svg>
  );
}

export function IconQuietDays(props: SVGProps<SVGSVGElement>) {
  // A low sun over a horizon: the slow week. Distinct from the
  // calendar-ish Timelines mark and the Business chart.
  return (
    <svg {...baseProps} {...props}>
      <path d="M4 17h16" />
      <path d="M7.5 17a4.5 4.5 0 0 1 9 0" />
      <path d="M12 6v2" />
      <path d="M6 9.5l1.4 1.4" />
      <path d="M18 9.5l-1.4 1.4" />
      <path d="M8 20.5h8" />
    </svg>
  );
}

export function IconSite(props: SVGProps<SVGSVGElement>) {
  // Globe-with-meridians glyph. Reads as "the public website" — and
  // sits visually distinct from the folio frame icon directly above
  // it in the sidebar.
  return (
    <svg {...baseProps} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 010 18" />
      <path d="M12 3a14 14 0 000 18" />
    </svg>
  );
}

export function IconTaste(props: SVGProps<SVGSVGElement>) {
  // Sparkle: the taste-trainer surfaces curated craft references, so a
  // small four-point sparkle reads as "discover / refine" without
  // implying a thumbs-up (which would prejudge the rating).
  return (
    <svg {...baseProps} {...props}>
      <path d="M12 3v4" />
      <path d="M12 17v4" />
      <path d="M3 12h4" />
      <path d="M17 12h4" />
      <path d="M5.5 5.5l2.5 2.5" />
      <path d="M16 16l2.5 2.5" />
      <path d="M18.5 5.5l-2.5 2.5" />
      <path d="M8 16l-2.5 2.5" />
    </svg>
  );
}
